import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

const Avatar = ({ 
  src, 
  name, 
  size = 'md', 
  className,
  showDecoration = true,
  type = 'user', // user, agency, client, creative
  isActive = false // When true, shows green rotating beam (for active timer)
}) => {
  const { getSeasonalDecoration, theme } = useTheme();
  const decoration = showDecoration ? getSeasonalDecoration() : null;
  
  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-20 h-20 text-2xl',
  };

  // Sizes for the active beam container (slightly larger)
  const activeContainerSizes = {
    xs: 'w-7 h-7',
    sm: 'w-9 h-9',
    md: 'w-11 h-11',
    lg: 'w-14 h-14',
    xl: 'w-[68px] h-[68px]',
    '2xl': 'w-[84px] h-[84px]',
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'agency':
        return 'bg-[var(--color-primary)]';
      case 'client':
        return 'bg-[var(--color-accent)]';
      case 'creative':
        return 'bg-[#6366f1]';
      default:
        return 'bg-[var(--color-primary)]/20';
    }
  };

  const getTextColor = () => {
    switch (type) {
      case 'agency':
      case 'creative':
        return 'text-white';
      case 'client':
        return 'text-[var(--color-primary)]';
      default:
        return 'text-[var(--color-primary)]';
    }
  };

  // If active, wrap in container with green rotating beam
  if (isActive) {
    return (
      <div className={cn("relative inline-block", className)}>
        {/* Active timer container with green rotating beam */}
        <div className={cn("relative", activeContainerSizes[size])}>
          {/* Green rotating beam */}
          <div 
            className="absolute inset-[-2px] rounded-full"
            style={{
              background: 'conic-gradient(from 0deg, transparent 0%, transparent 85%, #22c55e 92%, #4ade80 96%, transparent 100%)',
              animation: 'spin-clockwise 2.5s linear infinite',
            }}
          />
          {/* Inner avatar */}
          <div
            className={cn(
              "absolute inset-[2px] rounded-full overflow-hidden flex items-center justify-center font-semibold z-10",
              !src && getBackgroundColor(),
              !src && getTextColor()
            )}
            data-testid="avatar-active"
          >
            {src ? (
              <img 
                src={src} 
                alt={name || 'Avatar'} 
                className="w-full h-full object-cover"
              />
            ) : (
              <span>{getInitials(name)}</span>
            )}
          </div>
        </div>
        
        {/* Seasonal decoration */}
        {decoration && (
          <div 
            className={cn(
              "absolute -top-1 -right-1 text-sm pointer-events-none z-20",
              decoration.className
            )}
            style={{
              fontSize: size === 'xs' || size === 'sm' ? '0.75rem' : 
                       size === 'md' ? '0.875rem' : 
                       size === 'lg' ? '1rem' : '1.25rem'
            }}
          >
            {decoration.overlay}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative inline-block", className)}>
      {/* Main avatar */}
      <div
        className={cn(
          "rounded-full overflow-hidden flex items-center justify-center font-semibold",
          sizeClasses[size],
          !src && getBackgroundColor(),
          !src && getTextColor()
        )}
        data-testid="avatar"
      >
        {src ? (
          <img 
            src={src} 
            alt={name || 'Avatar'} 
            className="w-full h-full object-cover"
          />
        ) : (
          <span>{getInitials(name)}</span>
        )}
      </div>
      
      {/* Seasonal decoration */}
      {decoration && (
        <div 
          className={cn(
            "absolute -top-1 -right-1 text-sm pointer-events-none",
            decoration.className
          )}
          style={{
            fontSize: size === 'xs' || size === 'sm' ? '0.75rem' : 
                     size === 'md' ? '0.875rem' : 
                     size === 'lg' ? '1rem' : '1.25rem'
          }}
        >
          {decoration.overlay}
        </div>
      )}
    </div>
  );
};

export default Avatar;
